import { createElement, useCallback, memo, useEffect, useRef } from '@max/max';
import View from '@hfe/max-view';
import LinearGradient from '@hfe/max-linear-gradient';
import MaxButton from '@hfe/max-button';
import Text from '@hfe/max-text';
import './index.scss';
import { ActivityState } from '@/types';
import ActivitySubscribeSection from '../activity-subscribe-section';
import { BID_MAP, BID_TYPE, BUTTON_TYPE, TRIGGER_DRAW_EVENT, SHARE_BUTTON_TYPE } from '../../constants';
import Sensor from '@max/mbc-sensor';
import { useMonitorEngineContext } from '@nibfe/mcp-monitor-sdk';
import { Animated } from '@max/mbc-animated';
import AnimatedView from '@max/mbc-animate-view';
const GradientButton = memo(({ onPress, width, className, renderContent, status = 1, colors }) => {
    const isDisabled = status === 0;
    const handlePress = useCallback(() => {
        if (isDisabled)
            return;
        onPress === null || onPress === void 0 ? void 0 : onPress();
    }, [onPress, isDisabled]);
    const normalColors = colors || ['#FF9233', '#FF5500'];
    const bgColors = isDisabled ? ['#E5E5E5', '#E5E5E5'] : normalColors;
    return <LinearGradient style={{
        width: width || '100%',
        borderRadius: '80rpx'
        // opacity: isDisabled ? 0.2 : 1,
    }} colors={bgColors} start={{
        x: 0,
        y: 0
    }} end={{
        x: 1,
        y: 0
    }}>
      <MaxButton className={className || 'operate-btns__button'} style={{
        width: '100%'
    }} onPress={handlePress}>
        <View>
          {renderContent()}
        </View>
      </MaxButton>
    </LinearGradient>;
});
const renderShareText = (text) => {
    return <Text className="operate-btns__text">{text}</Text>;
};
const renderDrawText = (times, isDisabled) => {
    return <View className="draw-btns__container">
      <Text className="operate-btns__text" style={{
        color: isDisabled ? '#999999' : '#ffffff'
    }}>
        抽奖享半价
      </Text>
      <Text className="draw-btns__subscript" style={{
        color: isDisabled ? '#999999' : '#ffffff'
    }}>
        （ {times} 次）
      </Text>
    </View>;
};
const OperateBtns = ({ state, onDraw, onShare, activityId, drawModuleInfo, subscribeConfig, onCountdownEnd, onSubscribeStatusChange, onSubscribed, lxReport, vapVideoEnded, onBounceAnimEnd }) => {
    const ctx = useMonitorEngineContext();
    const drawChance = (drawModuleInfo === null || drawModuleInfo === void 0 ? void 0 : drawModuleInfo.drawChance) || 0;
    const handleDraw = useCallback((type) => {
        lxReport(BID_MAP.DRAW_BUTTON_MC, BID_TYPE.MC, {
            type
        });
        onDraw === null || onDraw === void 0 ? void 0 : onDraw();
    }, [onDraw, lxReport]);
    // 弹性缩放动画
    const scaleAnim = useRef(new Animated.Value(1)).current;
    const playBounceAnim = useCallback(() => {
        Animated.sequence([Animated.timing(scaleAnim, {
                toValue: 1.06,
                duration: 150,
                easing: Animated.Easing.out(Animated.Easing.quad),
                useNativeDriver: true
            }), Animated.timing(scaleAnim, {
                toValue: 0.97,
                duration: 120,
                easing: Animated.Easing.in(Animated.Easing.quad),
                useNativeDriver: true
            }), Animated.timing(scaleAnim, {
                toValue: 1.03,
                duration: 100,
                easing: Animated.Easing.out(Animated.Easing.quad),
                useNativeDriver: true
            }), Animated.timing(scaleAnim, {
                toValue: 1,
                duration: 80,
                easing: Animated.Easing.in(Animated.Easing.quad),
                useNativeDriver: true
            })]).start(() => {
            onBounceAnimEnd === null || onBounceAnimEnd === void 0 ? void 0 : onBounceAnimEnd();
        });
    }, [scaleAnim, onBounceAnimEnd]);
    // vapVideoEnded 变为 true 时触发一次弹性动画
    useEffect(() => {
        if (vapVideoEnded && state === ActivityState.UNRESERVED_CAN_DRAW) {
            playBounceAnim();
        }
    }, [vapVideoEnded]);
    // 监听 VapView 点击事件
    useEffect(() => {
        var _a;
        const handleTriggerDraw = () => {
            // 根据当前状态触发对应的抽奖逻辑
            if (state === ActivityState.UNRESERVED_CAN_DRAW) {
                handleDraw(BUTTON_TYPE.UNRESERVED_CAN_DRAW);
            }
            else if (state === ActivityState.DRAWN_HAS_CHANCES) {
                handleDraw(BUTTON_TYPE.DRAWN_HAS_CHANCES);
            }
        };
        // 注册事件监听
        (_a = ctx === null || ctx === void 0 ? void 0 : ctx._eventInstance) === null || _a === void 0 ? void 0 : _a.on(TRIGGER_DRAW_EVENT, handleTriggerDraw);
        // 组件卸载时移除监听
        return () => {
            var _a;
            (_a = ctx === null || ctx === void 0 ? void 0 : ctx._eventInstance) === null || _a === void 0 ? void 0 : _a.off(TRIGGER_DRAW_EVENT, handleTriggerDraw);
        };
    }, [state, handleDraw, ctx]);
    switch (state) {
        case ActivityState.UNRESERVED_CAN_DRAW:
            // 抽奖当天首次抽奖
            return <Sensor once onAppear={() => {
                lxReport(BID_MAP.BUTTON_MV, BID_TYPE.MV, {
                    type: BUTTON_TYPE.UNRESERVED_CAN_DRAW
                });
            }} className="operate-btns__container">
          <AnimatedView style={{
                width: '100%',
                transform: [{
                        scale: scaleAnim
                    }]
            }}>
            <GradientButton onPress={() => handleDraw(BUTTON_TYPE.UNRESERVED_CAN_DRAW)} className="operate-btns__button" renderContent={() => renderDrawText(drawChance, false)} status={1}/>
          </AnimatedView>
        </Sensor>;
        case ActivityState.DRAWN_NO_CHANCES:
            // 未中奖
            return <Sensor className="operate-btns__container" once onAppear={() => {
                lxReport(BID_MAP.BUTTON_MV, BID_TYPE.MV, {
                    type: BUTTON_TYPE.DRAWN_NO_CHANCES
                });
                lxReport(BID_MAP.SHARE_BUTTON_MV, BID_TYPE.MV, {
                    type: SHARE_BUTTON_TYPE.MAIN_SHARE
                });
            }}>
          <GradientButton onPress={onShare} className="operate-btns__button" renderContent={() => renderShareText('分享再抽')} status={1} width={'323rpx'}/>
          <GradientButton onPress={() => handleDraw(BUTTON_TYPE.DRAWN_NO_CHANCES)} className="operate-btns__button" renderContent={() => renderDrawText(drawChance, true)} status={0} width={'323rpx'}/>
        </Sensor>;
        case ActivityState.DRAWN_HAS_CHANCES:
            // 已经抽过奖但未中奖，且还有抽奖机会
            return <Sensor className="operate-btns__container" once onAppear={() => {
                lxReport(BID_MAP.SHARE_BUTTON_MV, BID_TYPE.MV, {
                    type: SHARE_BUTTON_TYPE.MAIN_SHARE
                });
                lxReport(BID_MAP.BUTTON_MV, BID_TYPE.MV, {
                    type: BUTTON_TYPE.DRAWN_HAS_CHANCES
                });
            }}>
          <GradientButton onPress={onShare} className="operate-btns__button" renderContent={() => renderShareText('分享')} status={1} width={'160rpx'} colors={['#FF7E11', '#FF7E11']}/>
          <GradientButton onPress={() => handleDraw(BUTTON_TYPE.DRAWN_HAS_CHANCES)} className="operate-btns__button" renderContent={() => renderDrawText(drawChance, false)} status={1} width={'488rpx'}/>
        </Sensor>;
        case ActivityState.REWARD_PACKAGE:
            // 如果已经抽中奖励包，则不展示按钮
            return null;
        default:
            // 未到开奖时间，展示倒计时和预约按钮
            return <ActivitySubscribeSection activityId={activityId} drawModuleInfo={drawModuleInfo} subscribeConfig={subscribeConfig} onCountdownEnd={onCountdownEnd} onSubscribeStatusChange={onSubscribeStatusChange} onSubscribed={onSubscribed} lxReport={lxReport}/>;
    }
};
export default memo(OperateBtns);
